import { LoadStatus } from '@servicetitan/cp-ui';
import { InputFieldState } from '@servicetitan/form-state';
import { injectable } from '@servicetitan/react-ioc';
import { ContactMethod, resendMfaCode, verifyMfaCode } from 'api';
import { FormState } from 'formstate';
import { computed, makeObservable, observable, runInAction } from 'mobx';

@injectable()
export class AuthMfaVerificationStore {
    @observable submitStatus = LoadStatus.None;
    @observable resendStatus = LoadStatus.None;
    @observable mfaSessionToken = '';
    @observable keepSigned = true;
    @observable codeLength = 6;
    @observable codeLifetimeMinutes = 10;
    @observable resendCooldownSeconds = 30;
    @observable mfaContactMethod?: ContactMethod;

    form = new FormState({
        code: new InputFieldState('').disableAutoValidation(),
    });

    constructor() {
        makeObservable(this);
    }

    @computed get canSubmit() {
        return (
            this.form.$.code.value.length === this.codeLength &&
            this.submitStatus !== LoadStatus.Loading
        );
    }

    init = (
        token: string,
        keepSigned: string | undefined,
        codeLength?: number,
        codeLifetimeMinutes?: number,
        resendCooldownSeconds?: number,
        mfaContactMethod?: ContactMethod
    ) => {
        runInAction(() => {
            this.mfaSessionToken = token;
            this.keepSigned = keepSigned !== 'false';
            this.codeLength = codeLength ?? 6;
            this.codeLifetimeMinutes = codeLifetimeMinutes ?? 10;
            this.resendCooldownSeconds = resendCooldownSeconds ?? 30;
            /*
             * Retain the initial mfaContactMethod across resend: only set when provided,
             * never clobber with undefined from a resend re-init that omits the field.
             */
            if (mfaContactMethod !== undefined) {
                this.mfaContactMethod = mfaContactMethod;
            }
            this.submitStatus = LoadStatus.None;
            this.resendStatus = LoadStatus.None;
            this.form.reset();
        });
    };

    submit = async () => {
        const code = this.form.$.code.value.trim();
        if (!code) {
            return;
        }

        runInAction(() => {
            this.submitStatus = LoadStatus.Loading;
        });

        try {
            await verifyMfaCode({
                mfaSessionToken: this.mfaSessionToken,
                code: code.toUpperCase(),
                keepSigned: this.keepSigned,
            });

            runInAction(() => {
                this.submitStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.submitStatus = LoadStatus.Error;
            });
        }
    };

    resend = async () => {
        if (this.resendStatus === LoadStatus.Loading) {
            return;
        }

        runInAction(() => {
            this.resendStatus = LoadStatus.Loading;
        });

        try {
            const response = await resendMfaCode({
                mfaSessionToken: this.mfaSessionToken,
            });

            runInAction(() => {
                this.mfaSessionToken = response.mfaSessionToken;
                this.resendStatus = LoadStatus.Ok;
                setTimeout(() => {
                    runInAction(() => {
                        this.resendStatus = LoadStatus.None;
                    });
                }, 3000);
            });
        } catch {
            runInAction(() => {
                this.resendStatus = LoadStatus.Error;
                setTimeout(() => {
                    runInAction(() => {
                        this.resendStatus = LoadStatus.None;
                    });
                }, 3000);
            });
        }
    };
}
